import type { ElementPart } from 'lit'
import type { VerticalRange } from './reveal'
import { nothing } from 'lit'
import { Directive, directive } from 'lit/directive.js'

// inline 作答區碰到面板上限、當前題放不下時，讓讀者看得出下面還有選項（Figma 298:2436）：
//   1. 底部淡出：還有內容被藏在下緣之下時掛 data-fade，CSS 在下緣畫 --peek-fade 高的 mask（見
//      active.ts）。只看實際內容，不看 scrollHeight：內容全部看得見、只是底部留白放不下時不淡出。
//   2. 淡出帶裡要有字：作答區的下緣往上收（--peek-inset），讓最後一列可見選項的一行文字落在
//      淡出帶中段 —— 上半看得清楚、往下淡掉，列的下緣被切掉。淡出帶只蓋到列的 padding 或分隔線時，
//      看起來就像已經到底。收的是整個面板：inset 寫在 .frame 上，面板上限扣掉它（geometry.ts），
//      選項區與按鈕列之間不留白。代價是換到放得下或收法不同的題時，面板高度跟著變。
// 量測與決策分兩層：peekInset／fadeBottom 是純算；syncPeek 量 DOM 再寫回兩個鉤子。

/** 選項列在捲動內容座標（內容頂＝0）裡的垂直範圍，與它第一行（text）、最後一行（last）文字的中線。 */
export interface PeekRow extends VerticalRange {
  text: number
  last: number
}

// 並排的選項（雙欄）垂直範圍重疊，併成同一列：列高與兩條文字中線都取較低的那格，切線對準時兩格的
// 那一行才都至少落在淡出帶中段。上下相鄰的兩列在縮放下可能量出零點幾 px 的重疊，所以重疊要超過
// ROW_OVERLAP_TOLERANCE 才算並排。
const ROW_OVERLAP_TOLERANCE = 0.5
function mergeRows(options: PeekRow[]): PeekRow[] {
  const rows: PeekRow[] = []
  for (const o of [...options].sort((a, b) => a.top - b.top)) {
    const last = rows.at(-1)
    if (last !== undefined && o.top < last.bottom - ROW_OVERLAP_TOLERANCE) {
      last.bottom = Math.max(last.bottom, o.bottom)
      last.text = Math.max(last.text, o.text)
      last.last = Math.max(last.last, o.last)
    }
    else {
      rows.push({ ...o })
    }
  }
  return rows
}

/**
 * 捲動區下緣要往上收多少 px。view 是不收時的可視高，content 是捲動內容總高，options 是各選項列，
 * fade 是下緣淡出帶的高。
 *
 * 看淡出帶的中線（view − fade / 2）落在切線那一列的哪裡：在第一行到最後一行文字中線之間（兩端各放寬
 * fade / 4），就有一行字正在淡出，不收。落在最後一行之下（字幾乎全亮、淡出只蓋到列底的留白），收到
 * 這一列最後一行的目標；落在第一行之上（字還沒進淡出帶），收到上一列最後一行的目標。目標是那一行的
 * 中線往下 fade / 2，但不超出列底。內容放得下、切線在第一列之上或已經過了最後一列（那裡沒有「列」
 * 可以露出）都不收；最多只收可視高的一半。
 */
export function peekInset(view: number, content: number, options: PeekRow[], fade: number): number {
  if (content <= view)
    return 0
  const rows = mergeRows(options)
  // 最後一列頂端在切線之上的列：切線穿過它，或落在它與下一列之間。
  let i = -1
  while ((rows[i + 1]?.top ?? Infinity) < view)
    i++
  const row = rows[i]
  if (row === undefined || (view >= row.bottom && i === rows.length - 1))
    return 0
  const aim = (r: PeekRow): number => Math.min(r.last + fade / 2, r.bottom)
  const mid = view - fade / 2
  if (mid >= row.text - fade / 4 && mid <= row.last + fade / 4)
    return 0
  let target = aim(row)
  if (mid < row.text - fade / 4) {
    const prev = rows[i - 1]
    if (prev === undefined)
      return 0
    target = aim(prev)
  }
  const inset = Math.round(view - target)
  return inset > 0 && inset <= view / 2 ? inset : 0
}

/**
 * 下緣之下還有沒露出的內容。contentEnd 是最後一段內容的下緣（捲動內容座標，不含底部留白）。
 * 1px 容差吸收縮放時的小數捲動位置。
 */
export function fadeBottom(scrollTop: number, clientHeight: number, contentEnd: number): boolean {
  return contentEnd - clientHeight - scrollTop > 1
}

// 內容的下緣在捲動內容座標裡不隨捲動改變，量一次存起來給 scroll 事件用。
const contentEnds = new WeakMap<HTMLElement, number>()

function syncFade(scroller: HTMLElement): void {
  const end = contentEnds.get(scroller) ?? scroller.scrollHeight
  scroller.toggleAttribute('data-fade', fadeBottom(scroller.scrollTop, scroller.clientHeight, end))
}

// 捲動區裡的內容盒：.page-step 自帶上下 padding（題與題的間距），所以量它的子節點；其餘直接子節點
// （預覽提示、送出提示）量本身。
const CONTENT_SEL = ':scope > :not(.page-step), :scope > .page-step > *'

// 選項第一行與最後一行文字的中線（viewport 座標）：文字框頂往下、框底往上各半個行高。行高是
// normal（算不出 px）時以字級的 1.2 倍估。沒有文字框時兩者都退回整列的中線。
function textLines(opt: Element, box: DOMRect): [number, number] {
  const text = opt.querySelector('.opt-text')
  if (text === null)
    return [(box.top + box.bottom) / 2, (box.top + box.bottom) / 2]
  const r = text.getBoundingClientRect()
  const cs = getComputedStyle(text)
  const line = Number.parseFloat(cs.lineHeight)
  const half = Math.min(Number.isFinite(line) ? line : Number.parseFloat(cs.fontSize) * 1.2, r.height) / 2
  return [r.top + half, r.bottom - half]
}

/**
 * 量 scroller 的版面，把 --peek-inset 寫到面板（.frame）上、data-fade 寫到 scroller。不收時的可視高
 * ＝父層 .answer-area 現在的 clientHeight（scroller 絕對定位鋪滿它）加回面板上已經寫著的 inset，所以
 * 前一次寫進去的 inset 不影響這一次的決策。淡出帶的高讀 scroller 上的 --peek-fade，與畫 mask 的 CSS
 * 同一個來源。
 */
export function syncPeek(scroller: HTMLElement): void {
  const panel = scroller.closest<HTMLElement>('.frame')
  const applied = Number.parseFloat(panel?.style.getPropertyValue('--peek-inset') ?? '') || 0
  const view = (scroller.parentElement?.clientHeight ?? 0) + applied
  const top = scroller.getBoundingClientRect().top - scroller.scrollTop
  const options = [...scroller.querySelectorAll('.opt')].map((o) => {
    const r = o.getBoundingClientRect()
    const [first, last] = textLines(o, r)
    return { top: r.top - top, bottom: r.bottom - top, text: first - top, last: last - top }
  })
  const fade = Number.parseFloat(getComputedStyle(scroller).getPropertyValue('--peek-fade')) || 0
  const inset = peekInset(view, scroller.scrollHeight, options, fade)
  if (inset > 0)
    panel?.style.setProperty('--peek-inset', `${inset}px`)
  else panel?.style.removeProperty('--peek-inset')
  const boxes = [...scroller.querySelectorAll(CONTENT_SEL)]
  contentEnds.set(scroller, boxes.length === 0
    ? scroller.scrollHeight
    : Math.min(scroller.scrollHeight, Math.max(...boxes.map(b => b.getBoundingClientRect().bottom - top))))
  syncFade(scroller)
}

// 綁在 .answer-scroll 上：每次 render 後（作答長出「其他」輸入框等）重算一次；面板尺寸或字型
// 載入改變列高時由 ResizeObserver 觸發；捲動只影響淡出。一個 directive 實例只對應一個 element
// part；換題時 active.ts 以 keyed 換掉整個捲動區，directive 也跟著換新。listener 與 observer 在
// 第一次 update 時掛上，只綁在這個捲動區與它的子節點上，所以捲動區被換掉時它們一起脫離 DOM、一起
// 被回收，不必用 AsyncDirective 處理斷開；斷開後 isConnected 為 false，排程到的量測直接略過。
class PeekDirective extends Directive {
  private el: HTMLElement | undefined
  private ro: ResizeObserver | undefined
  private readonly observed = new Set<Element>()
  private queued = false

  render(): typeof nothing {
    return nothing
  }

  override update(part: ElementPart): typeof nothing {
    if (this.el === undefined) {
      const el = this.el = part.element as HTMLElement
      el.addEventListener('scroll', () => syncFade(el), { passive: true })
      // 回呼裡寫回的 inset 會改 scroller 的尺寸；留到下一個 frame 再量，不在同一輪 RO 派送裡
      // 製造新的尺寸變化。
      if (typeof ResizeObserver !== 'undefined')
        this.ro = new ResizeObserver(() => requestAnimationFrame(() => this.schedule()))
    }
    this.schedule()
    return nothing
  }

  // 等整棵模板 commit 完（子節點此時才就位）再量；同一輪的多次觸發併成一次。
  private schedule(): void {
    if (this.queued)
      return
    this.queued = true
    queueMicrotask(() => {
      this.queued = false
      const el = this.el
      if (el?.isConnected !== true)
        return
      syncPeek(el)
      this.track(el)
    })
  }

  // 觀察捲動區自己（抓面板寬高變化）與每個子節點（抓字型載入等改變內容高、但不改面板高的
  // 情況）。不觀察外層 .answer-area：它跨題常駐，observe 它的舊 observer 會被它留住，每換一題
  // 多留一個。捲動區自己的尺寸也會因寫回 inset 而變，回呼重算後得到同一個 inset，就此停住。
  // 只對新出現的節點 observe：對已觀察的節點再 observe 會重送一次通知，變成無限重算；已經不在
  // 的節點 unobserve，不留著它們。
  private track(el: HTMLElement): void {
    const ro = this.ro
    if (ro === undefined)
      return
    const now = new Set<Element>([el, ...el.children])
    for (const node of this.observed) {
      if (!now.has(node)) {
        ro.unobserve(node)
        this.observed.delete(node)
      }
    }
    for (const node of now) {
      if (!this.observed.has(node)) {
        ro.observe(node)
        this.observed.add(node)
      }
    }
  }
}

export const peek = directive(PeekDirective)
